import React, { useEffect, useState } from 'react';
import './News.css';

export function parseNewsArchive(data) {
  if (!data || data.schemaVersion !== 1 || !Array.isArray(data.items)) {
    throw new Error('Invalid news archive');
  }
  const ids = new Set();
  const items = data.items.map((item) => {
    if (!item || ['id', 'date', 'sortDate', 'title', 'body', 'image'].some((key) => typeof item[key] !== 'string' || !item[key].trim())) {
      throw new Error('Incomplete news entry');
    }
    if (!/^[a-z0-9][a-z0-9-]{0,79}$/.test(item.id) || ids.has(item.id)) throw new Error('Invalid news id');
    ids.add(item.id);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(item.sortDate)) throw new Error('Invalid news date');
    const date = new Date(`${item.sortDate}T00:00:00Z`);
    if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== item.sortDate) throw new Error('Invalid news date');
    if (!/^\/content\/news-images\/[a-zA-Z0-9_-]+\.(jpg|jpeg|png|gif|webp)$/.test(item.image)) throw new Error('Invalid news image');
    const length = Array.from(item.body).length;
    if (length < 300 || length > 400) throw new Error('Invalid news body');
    if (item.note !== undefined && typeof item.note !== 'string') throw new Error('Invalid news note');
    if (item.link) {
      const url = new URL(item.link);
      if (url.protocol !== 'https:' || url.username || url.password || typeof item.linkLabel !== 'string' || !item.linkLabel.trim()) throw new Error('Invalid news link');
    }
    if (item.en !== undefined) {
      const en = item.en;
      if (!en || typeof en !== 'object' || Array.isArray(en) ||
          ['date', 'title', 'body', ...(item.note ? ['note'] : []), ...(item.link ? ['linkLabel'] : [])].some((key) => typeof en[key] !== 'string' || !en[key].trim()) ||
          Object.keys(en).some((key) => !['date', 'title', 'body', 'note', 'linkLabel'].includes(key) || typeof en[key] !== 'string')) {
        throw new Error('Invalid English news translation');
      }
    }
    return item;
  });
  return items.sort((a, b) => b.sortDate.localeCompare(a.sortDate));
}

function News({ language = 'zh' }) {
  const [newsEvents, setNewsEvents] = useState(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const isEnglish = language === 'en';

  useEffect(() => {
    const controller = new AbortController();
    setFailed(false);
    setNewsEvents(null);
    async function loadNews() {
      try {
        const response = await fetch('/content/news.json', { cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('News request failed');
        const items = parseNewsArchive(await response.json());
        if (!controller.signal.aborted) setNewsEvents(items);
      } catch (error) {
        if (!controller.signal.aborted) setFailed(true);
      }
    }
    loadNews();
    return () => controller.abort();
  }, [attempt]);

  return (
    <div className="news-page">
      <div className="news-page-container">
        <header className="news-page-header"><h1>{isEnglish ? 'News' : '新闻'}</h1></header>
        {failed ? (
          <div className="news-load-status" role="alert">
            <p>{isEnglish ? 'News could not be loaded. Please try again.' : '新闻暂时无法加载，请重试。'}</p>
            <button type="button" onClick={() => setAttempt((value) => value + 1)}>{isEnglish ? 'Retry' : '重试'}</button>
          </div>
        ) : newsEvents === null ? (
          <p className="news-load-status" role="status">{isEnglish ? 'Loading news…' : '正在加载新闻…'}</p>
        ) : newsEvents.length === 0 ? (
          <p className="news-load-status">{isEnglish ? 'No news yet.' : '暂无新闻。'}</p>
        ) : (
          <section className="timeline" aria-label={isEnglish ? 'News timeline' : '新闻时间线'} lang={isEnglish ? 'en' : 'zh-CN'}>
            <div className="timeline-line" aria-hidden="true" />
            {newsEvents.map((item, index) => {
              const content = isEnglish && item.en ? item.en : item;
              return (
              <article key={item.id} className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`} aria-labelledby={`${item.id}-title`} lang={isEnglish && item.en ? 'en' : 'zh-CN'}>
                <div className="timeline-marker" aria-hidden="true"><span className="pulse" /></div>
                <div className="timeline-card">
                  <div className="timeline-header"><span className="timeline-month">{content.date}</span></div>
                  <div className="timeline-media"><img src={item.image} alt={content.title} loading="lazy" decoding="async" /></div>
                  <div className="timeline-body">
                    <h2 className="timeline-title" id={`${item.id}-title`}>{content.title}</h2>
                    {content.note && <p className="news-source-note">{content.note}</p>}
                    <p className="timeline-text">{content.body}</p>
                    {item.link && <a className="news-museum-link" href={item.link} target="_blank" rel="noopener noreferrer">{content.linkLabel} <span aria-hidden="true">↗</span></a>}
                  </div>
                </div>
              </article>
            );
            })}
          </section>
        )}
        <footer className="game-footer"><div className="footer-text">© {new Date().getFullYear()} Video Game and Art Research Center, Peking University</div></footer>
      </div>
    </div>
  );
}

export default News;
